import {isMobileGameDevice} from '../hooks/useMobileGameViewport';

type AtlasFrame={filename:string;frame:{x:number;y:number;w:number;h:number};[key:string]:any};
type Page={frames:AtlasFrame[];[key:string]:any};
type Placement={source:number;x:number;y:number;w:number;h:number;page:number;dx:number;dy:number;dw:number;dh:number;valid:boolean};
const dimension=(image:any,axis:'width'|'height')=>Number(image[axis==='width'?'naturalWidth':'naturalHeight']||image[axis]);

/** Repack the current atlas only. Frame names, offsets and event timing are unchanged. */
export function repackCombatAtlas(images:any[],data:Page[],limit:number,canvasFactory=(w:number,h:number)=>{
 const canvas=document.createElement('canvas');canvas.width=w;canvas.height=h;return canvas;
}) {
 const size=Math.max(64,Math.floor(limit));
 const unique=new Map<string,Placement>(),frames:{frame:AtlasFrame;placement:Placement}[]=[];
 const inputPages=data.length===1&&Array.isArray(data[0]?.textures)?data[0].textures:data;
 images.forEach((image,source)=>{
  const page=inputPages[source]??inputPages[0];
  if(!Array.isArray(page?.frames))throw Error('Unsupported combat atlas metadata');
  page.frames.forEach((original:AtlasFrame)=>{
   const {x,y,w,h}=original.frame;
   const key=[source,x,y,w,h].join(':');let placement=unique.get(key);
   if(!placement){
    const valid=[x,y,w,h].every(Number.isFinite)&&x>=0&&y>=0&&w>0&&h>0&&x+w<=dimension(image,'width')&&y+h<=dimension(image,'height');
    const ratio=valid?Math.min(1,(size-2)/w,(size-2)/h):0;
    placement={source,x,y,w,h,page:0,dx:0,dy:0,dw:valid?Math.max(1,Math.floor(w*ratio)):1,dh:valid?Math.max(1,Math.floor(h*ratio)):1,valid};
    unique.set(key,placement);
   }
   frames.push({frame:original,placement});
  });
 });
 const pages:{width:number;height:number;frames:AtlasFrame[]}[]=[];
 let page=0,x=1,y=1,rowHeight=0;
 // Sort rectangles, not animation frames. This reduces decoded and GPU memory.
 Array.from(unique.values()).sort((a,b)=>b.dh-a.dh||b.dw-a.dw).forEach(p=>{
  if(x+p.dw+1>size){x=1;y+=rowHeight+2;rowHeight=0;}
  if(y+p.dh+1>size){page++;x=1;y=1;rowHeight=0;}
  if(!pages[page])pages[page]={width:1,height:1,frames:[]};
  p.page=page;p.dx=x;p.dy=y;
  pages[page].width=Math.max(pages[page].width,x+p.dw+1);pages[page].height=Math.max(pages[page].height,y+p.dh+1);
  x+=p.dw+2;rowHeight=Math.max(rowHeight,p.dh);
 });
 const canvases=pages.map(p=>canvasFactory(p.width,p.height));
 unique.forEach(p=>{
  const context=canvases[p.page].getContext('2d');if(!context)throw Error('Mobile texture canvas unavailable');
  if(p.valid)context.drawImage(images[p.source],p.x,p.y,p.w,p.h,p.dx,p.dy,p.dw,p.dh);
 });
 frames.forEach(({frame,placement:p})=>{
  const copied:AtlasFrame={...frame,frame:{x:p.dx,y:p.dy,w:p.dw,h:p.dh}};
  if(p.dw!==p.w||p.dh!==p.h){
   copied.mobileOriginalSize={width:frame.trimmed?frame.spriteSourceSize.w:p.w,height:frame.trimmed?frame.spriteSourceSize.h:p.h,sourceWidth:frame.sourceSize?.w??p.w,sourceHeight:frame.sourceSize?.h??p.h};
  }
  pages[p.page].frames.push(copied);
 });
 return {images:canvases,data:pages.map((p,i)=>({image:`mobile-runtime-${i}`,format:'RGBA8888',scale:1,size:{w:p.width,h:p.height},frames:p.frames}))};
}

export function restoreCombatFrameSizes(texture:any) {
 Object.values(texture?.frames??{}).forEach((frame:any)=>{
  const original=frame.customData?.mobileOriginalSize;if(!original)return;
  frame.width=original.width;frame.height=original.height;
  frame.halfWidth=original.width/2;frame.halfHeight=original.height/2;
  frame.data.sourceSize.w=original.sourceWidth;frame.data.sourceSize.h=original.sourceHeight;
 });
}

function installSpriteGeometry(pipeline:any) {
 if(!pipeline?.batchSprite||pipeline.__mobileSpriteGeometry)return;
 const batch=pipeline.batchSprite;
 pipeline.batchSprite=function(sprite:any,...args:any[]) {
  const frame=sprite.frame,original=frame?.customData?.mobileOriginalSize;
  if(!original||sprite.isCropped)return batch.call(this,sprite,...args);
  // Sprite batching uses cutWidth/cutHeight for destination geometry, unlike
  // Quad batching which uses frame.width/height. Keep UVs and source cuts in
  // physical pixels, but draw at the original logical size for this batch.
  const width=frame.cutWidth,height=frame.cutHeight;
  frame.cutWidth=original.cutWidth??original.width;
  frame.cutHeight=original.cutHeight??original.height;
  try{return batch.call(this,sprite,...args);}
  finally{frame.cutWidth=width;frame.cutHeight=height;}
 };
 pipeline.__mobileSpriteGeometry=true;
}

function installOnTextureManager(manager:any,rendererFor:(manager:any)=>any) {
 if(manager.__mobileCombatAtlas)return;
 const limitFor=(target:any)=>{
  const renderer=rendererFor(target);
  const supported=Number(renderer?.config?.maxTextureSize)||Number(renderer?.gl?.getParameter(renderer.gl.MAX_TEXTURE_SIZE))||2048;
  return Math.min(2048,supported);
 };
 const original=manager.addAtlasJSONArray;
 manager.addAtlasJSONArray=function(key:string,source:any,data:any,dataSource:any){
  const limit=limitFor(this);
  const images=Array.isArray(source)?source:[source];
  if(dataSource||!images.some(image=>dimension(image,'width')>limit||dimension(image,'height')>limit)){
   const texture=original.call(this,key,source,data,dataSource);if(!dataSource)restoreCombatFrameSizes(texture);return texture;
  }
  const packed=repackCombatAtlas(images,Array.isArray(data)?data:[data],limit);
  const texture=original.call(this,key,packed.images,packed.data);
  restoreCombatFrameSizes(texture);
  return texture;
 };
 const hash=manager.addAtlasJSONHash;
 if(hash)manager.addAtlasJSONHash=function(key:string,source:any,data:any,dataSource:any){
  const limit=limitFor(this),images=Array.isArray(source)?source:[source];
  if(dataSource||!images.some(image=>dimension(image,'width')>limit||dimension(image,'height')>limit))return hash.call(this,key,source,data,dataSource);
  const pages=(Array.isArray(data)?data:[data]).map((page:any)=>({...page,frames:Object.entries(page.frames).map(([filename,frame]:[string,any])=>({...frame,filename}))}));
  return this.addAtlasJSONArray(key,images,pages);
 };
 manager.__mobileCombatAtlas=true;
}

/** Covers level-up, UI and every other Phaser game, before any game is created. */
export function installMobileTextureCompatibility(phaser:any) {
 if(!isMobileGameDevice())return;
 installOnTextureManager(phaser.Textures.TextureManager.prototype,manager=>manager.game?.renderer);
 installSpriteGeometry(phaser.Renderer?.WebGL?.Pipelines?.TextureTintPipeline?.prototype);
}

/** Also supports isolated combat/replay entry points that do not use index.tsx. */
export function installMobileCombatTextures(scene:Phaser.Scene) {
 if(!isMobileGameDevice())return;
 installOnTextureManager(scene.textures,()=>scene.game.renderer);
 Object.values((scene.game.renderer as any)?.pipelines??{}).forEach(installSpriteGeometry);
}
